import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultWMS2040_R01HeaderDto, WMS2040_R01HeaderDto } from './Dto_WMS2040_R01';
import { useWMS2040_R01Store } from './Store_WMS2040_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS2040_R01-grid-column-state';

export interface Props {
	onRowClick?: (rowData: WMS2040_R01HeaderDto) => void;
	gridRef: React.RefObject<AgGridReact<WMS2040_R01HeaderDto> | null>;
}

export const Table_WMS2040_R01 = (props: Props) => {
	const { onRowClick, gridRef } = props;

	const {
		WMS2040_R01List,
		setSelectedWMS2040_R01,
		initSelectedWMS2040_R01,
		selectedRowInfo,
		setSelectedRowInfo,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useWMS2040_R01Store();

	const { unitList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const normalizedSelectedIds: number[] = selectedRowInfo ? [selectedRowInfo.valueIndex] : [];

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2040_R01List,
		defaultGridData: defaultWMS2040_R01HeaderDto,
		selectedRow: selectedRowInfo
			? WMS2040_R01List.find((item: WMS2040_R01HeaderDto) =>
				item.itemCd === selectedRowInfo.itemCd
				&& item.warehouseCd === selectedRowInfo.warehouseCd
				&& item.logicCenter === selectedRowInfo.logicCenter
				&& item.itemStatus === selectedRowInfo.itemStatus
				&& item.valueIndex === selectedRowInfo.valueIndex,
			)
			: undefined,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		logicCenterNm: '물류센터',
		warehouseNm: '창고명',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		itemStatus: '품목등급',
		itemUnit: '단위',
		currentQty: '현재고량',
		itemCost: '원가',
		stockAmount: '재고금액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'valueIndex')(params), []);

	const baseColumnDefs: ColDef<WMS2040_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '물류센터', colId: 'logicCenterNm', field: 'logicCenterNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'logicCenterNm'),
		},
		{
			headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'warehouseNm'),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
		},
		{
			headerName: '품목등급', colId: 'itemStatus', field: 'itemStatus', width: 80,
			cellStyle: (p) => getCellStyle(p, 'itemStatus'),
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 60,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
			cellStyle: (p) => getCellStyle(p, 'itemUnit'),
		},
		{
			headerName: '현재고량', colId: 'currentQty', field: 'currentQty', width: 100,
			valueFormatter: (p) => (p.value ? p.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'currentQty'),
		},
		{
			headerName: '원가', colId: 'itemCost', field: 'itemCost', width: 100,
			valueFormatter: (p) => (p.value ? p.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'itemCost'),
		},
		{
			headerName: '재고금액', colId: 'stockAmount', field: 'stockAmount', width: 120,
			valueFormatter: (p) => (p.value ? p.value.toLocaleString() : '0'),
			cellStyle: (p) => getCellStyle(p, 'stockAmount'),
		},
	], [headerNames, unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS2040_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS2040_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => {
		const totals = WMS2040_R01List.reduce((acc, item) => ({
			currentQty: acc.currentQty + (Number(item.currentQty) || 0),
			stockAmount: acc.stockAmount + (Number(item.stockAmount) || 0),
		}), { currentQty: 0, stockAmount: 0 });

		return [{
			valueIndex: '',
			rowIndex: '',
			compNm: `${WMS2040_R01List.length.toLocaleString()}건`,
			currentQty: totals.currentQty,
			stockAmount: totals.stockAmount,
			itemCost: '',
			editable: false,
		}];
	}, [WMS2040_R01List]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS2040_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isExactSameRow = selectedRowInfo && (
			selectedRowInfo.itemCd === rowData.itemCd
			&& selectedRowInfo.warehouseCd === rowData.warehouseCd
			&& selectedRowInfo.logicCenter === rowData.logicCenter
			&& selectedRowInfo.itemStatus === rowData.itemStatus
			&& selectedRowInfo.valueIndex === rowData.valueIndex
		);

		if (isExactSameRow) {
			setSelectedWMS2040_R01(undefined);
			setSelectedRowInfo(undefined);
			onRowClick?.(rowData);
		} else {
			setSelectedWMS2040_R01(rowData.itemCd);
			setSelectedRowInfo({
				itemCd: rowData.itemCd,
				warehouseCd: rowData.warehouseCd,
				logicCenter: rowData.logicCenter,
				itemStatus: rowData.itemStatus,
				valueIndex: Number(rowData.valueIndex),
			});
			onRowClick?.(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedRowInfo, setSelectedWMS2040_R01, setSelectedRowInfo, onRowClick, setDetailScrollTop]);

	const rowClassRules = useMemo(() =>
		createRowClassRules<WMS2040_R01HeaderDto>({
			idKey: 'valueIndex',
			selectedList: normalizedSelectedIds,
			deletedList: [],
		}), [createRowClassRules, normalizedSelectedIds]);

	const handleInitSelected = useCallback(() => {
		initSelectedWMS2040_R01();
		setSelectedRowInfo(undefined);
	}, [initSelectedWMS2040_R01, setSelectedRowInfo]);

	useEffect(() => { ensureSelectedRow(); }, [WMS2040_R01List, selectedRowInfo]);

	return (
		<GridContextWrapper<WMS2040_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef as React.RefObject<AgGridReact<WMS2040_R01HeaderDto>>}
			paddedGridData={paddedGridData}
			defaultRowData={defaultWMS2040_R01HeaderDto}
			initSelected={handleInitSelected}
			allSelected={() => {}}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS2040_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default Table_WMS2040_R01;
